import { cn } from '@/lib/cn'
import { motion } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import { type ReactNode, useId } from 'react'

interface Tab {
  id: string
  label: ReactNode
  icon?: LucideIcon
  count?: number
  disabled?: boolean
}

interface Props {
  tabs: Tab[]
  value: string
  onChange: (id: string) => void
  className?: string
}

export function Tabs({ tabs, value, onChange, className }: Props) {
  const underlineId = useId()
  return (
    <div
      role="tablist"
      className={cn('flex gap-1 border-b border-border', className)}
    >
      {tabs.map((t) => {
        const active = t.id === value
        const Icon = t.icon
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={t.disabled}
            onClick={() => onChange(t.id)}
            className={cn(
              'relative -mb-px flex items-center gap-2 px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-50',
              active
                ? 'text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
            <span>{t.label}</span>
            {typeof t.count === 'number' ? (
              <span
                className={cn(
                  'inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {t.count}
              </span>
            ) : null}
            {active ? (
              <motion.span
                layoutId={underlineId}
                className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary"
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
